import React, { useState, useEffect, useRef } from 'react';
import Header from './Header';
import Footer from './Footer';
import ProductCard from './ProductCard';
import PageLayout from './PageLayout';
import ProductCardSkeleton from './ProductCardSkeleton';
import SEOHead from './SEOHead';
import { apiGet } from '../utils/api';
import { useDarkMode } from '../contexts/DarkModeContext';

const PRODUCTS_PER_PAGE = 20;

// Helper to shuffle an array
function shuffle(array) {
  const arr = array.slice();
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

// Hero Banner Component
const HeroBanner = ({ isDarkMode, filterBarRef }) => (
  <div className={`py-4 sm:py-6 lg:py-8 transition-colors duration-300 ${
    isDarkMode 
      ? 'bg-[#0f0f0f]' 
      : 'bg-gradient-to-br from-blue-50 to-white'
  }`}>
    <div className="max-w-[1525px] mx-auto px-4 sm:px-5">
      <div className="w-full flex justify-center relative" style={{zIndex: 1}}>
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{zIndex: 0}}>
          <img 
            src="/images/categories/facebook.svg" 
            alt="Facebook background" 
            width="320" height="320"
            style={{
              width: '200px',
              height: '200px',
              filter: 'blur(40px)',
              opacity: isDarkMode ? 0.15 : 0.12,
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            }}
            className="sm:w-[320px] sm:h-[320px] sm:blur-[50px]"
            loading="eager"
            decoding="async"
          />
        </div>
        <div className={`rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-10 flex flex-col items-center justify-center text-white w-full mx-4 max-w-[1495px] mx-auto relative transition-all duration-500 ${
          isDarkMode 
            ? 'bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] shadow-2xl shadow-black/50 border border-blue-500/20' 
            : 'bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 shadow-xl'
        }`} style={{zIndex: 1}}>
          {/* Animated background elements for dark mode */}
          {isDarkMode && (
            <div className="absolute inset-0 overflow-hidden rounded-2xl sm:rounded-3xl">
              <div className="absolute -top-6 -left-6 sm:-top-10 sm:-left-10 w-12 h-12 sm:w-20 sm:h-20 bg-blue-400/10 rounded-full blur-xl animate-pulse"></div>
              <div className="absolute -bottom-6 -right-6 sm:-bottom-10 sm:-right-10 w-20 h-20 sm:w-32 sm:h-32 bg-blue-300/10 rounded-full blur-xl animate-pulse" style={{animationDelay: '1s'}}></div>
              <div className="absolute top-1/2 left-1/4 w-10 h-10 sm:w-16 sm:h-16 bg-blue-500/10 rounded-full blur-lg animate-pulse" style={{animationDelay: '2s'}}></div>
            </div>
          )}
          
          <div className="relative z-10">
            <h1 className={`text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold mb-3 sm:mb-4 text-center transition-colors duration-300 ${
              isDarkMode 
                ? 'text-white drop-shadow-lg' 
                : 'text-white'
            }`}>
              Buy & Sell Facebook Pages
            </h1>
            <p className={`text-sm sm:text-base md:text-lg lg:text-xl mb-6 sm:mb-8 text-center max-w-2xl transition-colors duration-300 ${
              isDarkMode 
                ? 'text-blue-100' 
                : 'text-blue-50'
            }`}>
              Discover top-quality, verified Facebook pages for your brand or business. Browse a wide range of pages, all vetted for authenticity and performance.
            </p>
            <div className="flex items-center justify-center mb-6 sm:mb-8">
              <div className={`w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 rounded-lg sm:rounded-xl flex items-center justify-center transition-all duration-300 hover:scale-110 ${
                isDarkMode 
                  ? 'bg-gradient-to-br from-blue-600 to-blue-700 shadow-lg shadow-blue-500/25' 
                  : 'bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg'
              }`}>
                <img src="/images/categories/facebook.svg" alt="Facebook" className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />
              </div>
            </div>
            <div className="flex justify-center w-full">
              <button
                className={`px-6 sm:px-8 py-2.5 sm:py-3 rounded-full font-bold transition-all duration-300 text-sm sm:text-base lg:text-lg ${
                  isDarkMode 
                    ? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/40 transform hover:scale-105' 
                    : 'bg-blue-600 hover:bg-blue-700 shadow-lg'
                }`}
                onClick={() => {
                  if (filterBarRef.current) {
                    filterBarRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }}
              >
                Explore Facebook Pages
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const BuyFacebookPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [seoSettings, setSeoSettings] = useState(null);
  const [filters, setFilters] = useState({
    category: 'all',
    minPrice: '',
    maxPrice: '',
    minFollowers: '',
    maxFollowers: '',
    monetization: 'all',
    search: ''
  });
  const filterBarRef = useRef(null);
  const { isDarkMode } = useDarkMode();

  // Fetch SEO settings for buy Facebook page
  useEffect(() => {
    const fetchSeoSettings = async () => {
      try {
        const res = await apiGet('/api/page-seo/page/buy-facebook-page');
        const data = await res.json();
        setSeoSettings(data);
      } catch (error) {
        console.error('Error loading SEO settings:', error);
      }
    };
    fetchSeoSettings();
  }, []);

  // Fetch Facebook products from backend
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        // Use the public endpoint
        const response = await apiGet('/api/products/boosted-random');
        if (!response.ok) {
          throw new Error('Failed to fetch products');
        }
        const data = await response.json();
        setProducts(Array.isArray(data) ? data.filter(p => p.status === 'published') : []);
        setError('');
      } catch (err) {
        setError('Failed to load products. Please try again later.');
        console.error('Error fetching products:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  // Filter products based on selected filters
  const filteredProducts = products
    .filter(product => (product.platform === 'facebook' || product.accountType === 'facebook'))
    .filter(product => {
      if (filters.category !== 'all' && product.stats?.category !== filters.category) return false;
      if (filters.minPrice && product.price < Number(filters.minPrice)) return false;
      if (filters.maxPrice && product.price > Number(filters.maxPrice)) return false;
      if (filters.minFollowers && product.stats?.followers < Number(filters.minFollowers)) return false;
      if (filters.maxFollowers && product.stats?.followers > Number(filters.maxFollowers)) return false;
      if (filters.monetization !== 'all') {
        const monetizedStatus = product.stats?.monetized || product.stats?.monetization || product.monetized || product.monetization;
        const isMonetized = String(monetizedStatus).toLowerCase() === 'yes' || String(monetizedStatus).toLowerCase() === 'monetized' || monetizedStatus === true;
        if (filters.monetization === 'monetized' && !isMonetized) return false;
        if (filters.monetization === 'non-monetized' && isMonetized) return false;
      }
      if (filters.search && !product.title.toLowerCase().includes(filters.search.toLowerCase())) return false;
      return true;
    });

  // Before rendering, combine boosted and shuffled non-boosted for pagination
  const boosted = filteredProducts.filter(p => p.boosted);
  const nonBoosted = shuffle(filteredProducts.filter(p => !p.boosted));
  const combinedProducts = [...boosted, ...nonBoosted];
  const paginatedProducts = combinedProducts.slice(
    (currentPage - 1) * PRODUCTS_PER_PAGE,
    currentPage * PRODUCTS_PER_PAGE
  );

  const totalPages = Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE);

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const renderPagination = () => (
    <div className="flex items-center gap-4 mt-10">
      {[...Array(totalPages)].map((_, idx) => (
        <button
          key={idx}
          onClick={() => setCurrentPage(idx + 1)}
          className={`w-14 h-12 rounded-full border text-lg font-medium transition-colors duration-150 focus:outline-none ${
            currentPage === idx + 1
              ? 'bg-[#07003b] text-white border-[#07003b]'
              : isDarkMode
                ? 'bg-[#2a2a2a] text-gray-200 border-gray-600 hover:bg-[#3a3a3a]'
                : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-100'
          }`}
        >
          {idx + 1}
        </button>
      ))}
      <button
        onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
        disabled={currentPage === totalPages}
        className={`w-20 h-12 rounded-full border text-lg font-medium transition-colors duration-150 focus:outline-none ${
          currentPage === totalPages
            ? isDarkMode
              ? 'bg-[#1a1a1a] text-gray-500 border-gray-700 cursor-not-allowed'
              : 'bg-gray-200 text-gray-400 border-gray-200 cursor-not-allowed'
            : 'bg-red-600 text-white border-red-600 hover:bg-red-700'
        }`}
      >
        Next
      </button>
    </div>
  );

  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  return (
    <div className={`min-h-screen flex flex-col transition-colors duration-300 ${
      isDarkMode ? 'bg-[#0f0f0f]' : 'bg-gray-50'
    }`}>
      <SEOHead
        title={seoSettings?.title || 'Buy Facebook Page - YTProud'}
        description={seoSettings?.description || 'Buy verified Facebook pages with followers and engagement. Secure marketplace for Facebook page purchases.'}
        keywords={seoSettings?.keywords || 'buy facebook page, facebook page for sale, verified facebook pages'}
        ogTitle={seoSettings?.ogTitle || 'Buy Facebook Page - YTProud'}
        ogDescription={seoSettings?.ogDescription || 'Buy verified Facebook pages with followers and engagement'}
        ogImage={seoSettings?.ogImage || '/images/facebook-og.jpg'}
        twitterTitle={seoSettings?.twitterTitle || 'Buy Facebook Page - YTProud'}
        twitterDescription={seoSettings?.twitterDescription || 'Buy verified Facebook pages with followers and engagement'}
        twitterImage={seoSettings?.twitterImage || '/images/facebook-og.jpg'}
        canonical={seoSettings?.canonical || 'https://ytproud.com/buy-facebook-page'}
        noindex={seoSettings?.noindex || false}
        nofollow={seoSettings?.nofollow || false}
        schemaEnabled={seoSettings?.schemaEnabled || false}
        schemaJsonLd={seoSettings?.customSchemaJsonLd}
      />
      <Header />
      {/* Modern Hero Section - Banner */}
      <HeroBanner isDarkMode={isDarkMode} filterBarRef={filterBarRef} />
      <PageLayout>
        {/* Filter Bar */}
        <div className="max-w-[1495px] mx-auto">
          <div ref={filterBarRef} className={`flex flex-wrap items-center justify-between rounded-xl shadow px-6 py-4 mb-8 transition-colors duration-300 ${
            isDarkMode 
              ? 'bg-[#1a1a1a] shadow-lg shadow-black/30' 
              : 'bg-white'
          }`}>
            <div className="flex items-center gap-4">
              <span className={`font-semibold transition-colors duration-300 ${
                isDarkMode ? 'text-gray-200' : 'text-gray-700'
              }`}>Filter by:</span>
              <select 
                name="category"
                value={filters.category}
                onChange={handleFilterChange}
                className={`rounded px-3 py-2 transition-colors duration-300 ${
                  isDarkMode 
                    ? 'bg-[#2a2a2a] border-gray-600 text-gray-200 focus:border-blue-500' 
                    : 'border-gray-300'
                }`}
              >
                <option value="all">All Categories</option>
                <option value="Business">Business</option>
                <option value="Community">Community</option>
                <option value="Entertainment">Entertainment</option>
                <option value="News">News</option>
                <option value="Education">Education</option>
                <option value="Fashion & Style">Fashion & Style</option>
                <option value="Fitness & Sports">Fitness & Sports</option>
                <option value="Gaming">Gaming</option>
                <option value="Pets & Animals">Pets & Animals</option>
                <option value="Travel">Travel</option>
                <option value="Vlogs">Vlogs</option>
                <option value="Models & Celebrities">Models & Celebrities</option>
              </select>
              <select 
                name="monetization"
                value={filters.monetization}
                onChange={handleFilterChange}
                className={`rounded px-3 py-2 transition-colors duration-300 ${
                  isDarkMode 
                    ? 'bg-[#2a2a2a] border-gray-600 text-gray-200 focus:border-blue-500' 
                    : 'border-gray-300'
                }`}
              >
                <option value="all">All Monetization</option>
                <option value="monetized">Monetized</option>
                <option value="non-monetized">Non-Monetized</option>
              </select>
              <input 
                type="number" 
                name="minPrice"
                value={filters.minPrice}
                onChange={handleFilterChange}
                min="0" 
                placeholder="Lowest price" 
                className={`rounded px-3 py-2 w-32 transition-colors duration-300 ${
                  isDarkMode 
                    ? 'bg-[#2a2a2a] border-gray-600 text-gray-200 placeholder-gray-400 focus:border-blue-500' 
                    : 'border-gray-300'
                }`}
              />
              <span className={`transition-colors duration-300 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-400'
              }`}>-</span>
              <input 
                type="number" 
                name="maxPrice"
                value={filters.maxPrice}
                onChange={handleFilterChange}
                min="0" 
                placeholder="Highest price" 
                className={`rounded px-3 py-2 w-32 transition-colors duration-300 ${
                  isDarkMode 
                    ? 'bg-[#2a2a2a] border-gray-600 text-gray-200 placeholder-gray-400 focus:border-blue-500' 
                    : 'border-gray-300'
                }`}
              />
              <span className={`mx-2 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-400'
              }`}>|</span>
              <input 
                type="number" 
                name="minFollowers"
                value={filters.minFollowers}
                onChange={handleFilterChange}
                min="0" 
                placeholder="Min followers" 
                className={`rounded px-3 py-2 w-32 transition-colors duration-300 ${
                  isDarkMode 
                    ? 'bg-[#2a2a2a] border-gray-600 text-gray-200 placeholder-gray-400 focus:border-blue-500' 
                    : 'border-gray-300'
                }`}
              />
              <span className={`transition-colors duration-300 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-400'
              }`}>-</span>
              <input 
                type="number" 
                name="maxFollowers"
                value={filters.maxFollowers}
                onChange={handleFilterChange}
                min="0" 
                placeholder="Max followers" 
                className={`rounded px-3 py-2 w-32 transition-colors duration-300 ${
                  isDarkMode 
                    ? 'bg-[#2a2a2a] border-gray-600 text-gray-200 placeholder-gray-400 focus:border-blue-500' 
                    : 'border-gray-300'
                }`}
              />
            </div>
            <input 
              type="text" 
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              placeholder="Search Facebook pages..." 
              className={`rounded px-4 py-2 w-64 transition-colors duration-300 ${
                isDarkMode 
                  ? 'bg-[#2a2a2a] border-gray-600 text-gray-200 placeholder-gray-400 focus:border-blue-500' 
                  : 'border-gray-300'
              }`}
            />
          </div>
        {/* Product Grid */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-3 lg:gap-6 max-w-[1495px] mx-auto mt-12">
            {Array.from({ length: 20 }).map((_, i) => <ProductCardSkeleton key={i} />)}
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-red-600 text-lg">{error}</div>
          </div>
        ) : paginatedProducts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <svg xmlns="http://www.w3.org/2000/svg" className={`h-16 w-16 mb-4 transition-colors duration-300 ${
              isDarkMode ? 'text-gray-600' : 'text-gray-300'
            }`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15z" />
            </svg>
            <div className={`text-xl font-semibold mb-2 transition-colors duration-300 ${
              isDarkMode ? 'text-gray-300' : 'text-gray-500'
            }`}>No pages match your filters</div>
            <div className={`text-base transition-colors duration-300 ${
              isDarkMode ? 'text-gray-500' : 'text-gray-400'
            }`}>Try adjusting your search or filter options.</div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-3 lg:gap-6 max-w-[1495px] mx-auto">
            {paginatedProducts.map((product, i) => (
              <ProductCard
                key={product._id || i + (currentPage - 1) * PRODUCTS_PER_PAGE}
                product={product}
                width={360}
              />
            ))}
          </div>
        )}
        {/* Pagination Controls */}
        {!loading && !error && paginatedProducts.length > 0 && (
          <div className="max-w-[1495px] mx-auto">{renderPagination()}</div>
        )}
        </div>
      </PageLayout>
      <Footer />
    </div>
  );
};

export default BuyFacebookPage; 